import { useEffect, useState } from "react";
import Script from "next/script";
import { useRouter } from "next/router";

export const FACEBOOK_PIXEL_ID = "211579130785249";

const GTM_ID = "GTM-KG2W7JS2";

const GA_MEASUREMENT_ID = "G-TNY34G5WP8";

const GOOGLE_ADS_ID = "AW-16652136146";

const CLARITY_ID = "wmwdn0itv9";

const HOTJAR_ID = "3368320";

const SMARTLOOK_KEY =
  "cb6d1d5573f9110ae733a0d70384c93e5519592a";

const GTM_SNIPPET = `
(function(w,d,s,l,i) {
  w[l] = w[l] || [];

  w[l].push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l != 'dataLayer' ? '&l=' + l : '';

  j.async = true;

  j.src =
    'https://www.googletagmanager.com/gtm.js?id=' +
    i +
    dl;

  f.parentNode.insertBefore(j, f);

})(
  window,
  document,
  'script',
  'dataLayer',
  '${GTM_ID}'
);
`;

const GOOGLE_TAG_INIT_SNIPPET = `
window.dataLayer = window.dataLayer || [];

window.gtag =
  window.gtag ||
  function() {
    window.dataLayer.push(arguments);
  };

window.gtag('js', new Date());

window.gtag(
  'config',
  '${GOOGLE_ADS_ID}'
);

window.gtag(
  'config',
  '${GA_MEASUREMENT_ID}',
  {
    send_page_view: false
  }
);

window.__GA4_CONFIGURED__ = true;

window.gtag(
  'event',
  'page_view',
  {
    page_title: document.title,
    page_location: window.location.href,
    page_path:
      window.location.pathname +
      window.location.search,
    send_to: '${GA_MEASUREMENT_ID}'
  }
);
`;

const FACEBOOK_PIXEL_SNIPPET = `
if (!window.__FBQ_LOADED__) {
  window.__FBQ_LOADED__ = true;

  !function(f,b,e,v,n,t,s) {

    if (f.fbq) return;

    n = f.fbq = function() {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };

    if (!f._fbq) {
      f._fbq = n;
    }

    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];

    t = b.createElement(e);
    t.async = true;
    t.src = v;

    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);

  }(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js'
  );

  fbq('init', '${FACEBOOK_PIXEL_ID}');
  fbq('track', 'PageView');
}
`;

const CLARITY_SNIPPET = `
if (!window.__CLARITY_LOADED__) {
  window.__CLARITY_LOADED__ = true;

  (function(c,l,a,r,i,t,y) {

    c[a] =
      c[a] ||
      function() {
        (c[a].q = c[a].q || []).push(arguments);
      };

    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;

    y = l.getElementsByTagName(r)[0];

    y.parentNode.insertBefore(t, y);

  })(
    window,
    document,
    "clarity",
    "script",
    "${CLARITY_ID}"
  );
}
`;

const HOTJAR_SNIPPET = `
if (!window.__HOTJAR_LOADED__) {
  window.__HOTJAR_LOADED__ = true;

  (function(h,o,t,j,a,r) {

    h.hj =
      h.hj ||
      function() {
        (h.hj.q = h.hj.q || []).push(arguments);
      };

    h._hjSettings = {
      hjid: ${HOTJAR_ID},
      hjsv: 6
    };

    a = o.getElementsByTagName('head')[0];

    r = o.createElement('script');

    r.async = 1;

    r.src =
      t +
      h._hjSettings.hjid +
      j +
      h._hjSettings.hjsv;

    a.appendChild(r);

  })(
    window,
    document,
    'https://static.hotjar.com/c/hotjar-',
    '.js?sv='
  );
}
`;

const SMARTLOOK_SNIPPET = `
if (!window.__SMARTLOOK_LOADED__) {
  window.__SMARTLOOK_LOADED__ = true;

  var o =
    window.smartlook =
    function() {
      o.api.push(arguments);
    };

  o.api = [];

  var h =
    document.getElementsByTagName('head')[0];

  var c =
    document.createElement('script');

  c.async = true;
  c.type = 'text/javascript';
  c.charset = 'utf-8';

  c.src =
    'https://web-sdk.smartlook.com/recorder.js';

  h.appendChild(c);
}

if (typeof window.smartlook === 'function') {
  window.smartlook(
    'init',
    '${SMARTLOOK_KEY}',
    {
      region: 'eu'
    }
  );
}
`;

export default function DelayedTrackers({
  replayDelayMs = 12000,
}) {
  const router = useRouter();

  const [loadReplayTools, setLoadReplayTools] =
    useState(false);

  /*
   * First page_view is sent when GA4 initializes.
   *
   * This handles subsequent Next.js client-side
   * navigations without requiring a full reload.
   */
  useEffect(() => {
    const handleRouteChange = (url) => {
      if (
        typeof window === "undefined" ||
        typeof window.gtag !== "function" ||
        !window.__GA4_CONFIGURED__
      ) {
        return;
      }

      window.gtag(
        "event",
        "page_view",
        {
          page_title: document.title,
          page_location: window.location.href,
          page_path: url,
          send_to: GA_MEASUREMENT_ID,
        },
      );
    };

    router.events.on(
      "routeChangeComplete",
      handleRouteChange,
    );

    return () => {
      router.events.off(
        "routeChangeComplete",
        handleRouteChange,
      );
    };
  }, [router.events]);

  /*
   * Replay / heatmap tools are delayed because
   * they are not required during initial rendering.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadReplayTools(true);
    }, replayDelayMs);

    return () => clearTimeout(timer);
  }, [replayDelayMs]);

  return (
    <>
      {/* Google Tag Manager */}
      <Script
        id="google-tag-manager"
        strategy="afterInteractive"
      >
        {GTM_SNIPPET}
      </Script>

      {/* Google Analytics + Google Ads */}
      <Script
        id="google-gtag"
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />

      <Script
        id="google-gtag-init"
        strategy="afterInteractive"
      >
        {GOOGLE_TAG_INIT_SNIPPET}
      </Script>

      {/* Facebook Pixel */}
      <Script
        id="facebook-pixel"
        strategy="lazyOnload"
      >
        {FACEBOOK_PIXEL_SNIPPET}
      </Script>

      {/* Delayed replay / heatmap tools */}
      {loadReplayTools && (
        <>
          <Script
            id="microsoft-clarity"
            strategy="afterInteractive"
          >
            {CLARITY_SNIPPET}
          </Script>

          <Script
            id="hotjar"
            strategy="afterInteractive"
          >
            {HOTJAR_SNIPPET}
          </Script>

          <Script
            id="smartlook"
            strategy="afterInteractive"
          >
            {SMARTLOOK_SNIPPET}
          </Script>
        </>
      )}
    </>
  );
}